import * as Toast from '@radix-ui/react-toast'
import * as EmailValidator from 'email-validator'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { NoAccountText, TextInput } from '@/components/widget-components'
import { usePageStateStore } from '@/reducers'
import { usePasswordlessAuth } from '@/hooks/usePasswordlessAuth'
import { PanelLayout } from './PanelLayout'
import * as Sentry from '@sentry/react'
import { track } from '@/utils/analytics'

const EmailLoginPanelInner = ({ landing }: { landing: boolean }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [inputError, setInputError] = useState('')
  const [toastOpen, setToastOpen] = useState(false)

  const setAuthToken = usePageStateStore.use.setAuthToken()
  const setLoggedInUser = usePageStateStore.use.setEmail()
  const redirectPath = usePageStateStore.use.redirectPath()
  const setProtectedRouteRedirectPath =
    usePageStateStore.use.setProtectedRouteRedirectPath()

  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo =
    location.state?.from?.pathname || (landing ? '/Dashboard' : '/')

  const errorMessages = useMemo(
    () => ({
      sendFailed: t('login.errors.unableToLogin'),
      timedOut: t('login.errors.signInTimedOut'),
      unexpected: t('login.errors.unexpectedError'),
    }),
    [t]
  )

  const onVerified = useCallback(
    ({
      email: verifiedEmail,
      accessToken,
    }: {
      email: string
      accessToken: string
    }) => {
      setAuthToken(accessToken)
      setLoggedInUser(verifiedEmail)
      navigate(redirectTo, { replace: true })
    },
    [navigate, redirectTo, setAuthToken, setLoggedInUser]
  )

  const {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading,
    errorMessage,
    lastSendSucceeded,
    resetDenied,
  } = usePasswordlessAuth({
    client: 'widget',
    sentryComponent: 'EmailLoginPanel',
    errorMessages,
    onVerified,
  })

  const triggerLogin = async () => {
    if (!email?.length || !EmailValidator.validate(email)) {
      setInputError(t('login.errors.invalidEmail'))
      return
    }
    if (!redirectPath) {
      setProtectedRouteRedirectPath('/dashboard')
    }
    setInputError('')
    Sentry.setUser({ email })
    track('login_email_submitted', { entry: landing ? 'landing' : 'widget' })
    await sendMagicLink({ email })
  }

  const displayedError = inputError || errorMessage

  if (denied) {
    return (
      <PanelLayout titleKey="welcome.title">
        <div className="flex flex-col gap-5">
          <h2
            data-testid="text-login-denied-title"
            className="text-lg font-normal uppercase"
          >
            {t('login.denied.title')}
          </h2>
          <div className="text-base" data-testid="text-login-denied-status">
            {t('login.denied.status')}
          </div>
          <Button
            onClick={resetDenied}
            data-testid="btn-login-denied-try-again"
            className="inline-flex items-center py-8 px-4 w-full justify-center mt-5"
          >
            {t('login.denied.tryAgain')}
          </Button>
        </div>
      </PanelLayout>
    )
  }

  if (waitingForApproval) {
    return (
      <PanelLayout titleKey="welcome.title">
        <div className="flex flex-col gap-5">
          <h2 className="text-lg font-normal uppercase">
            {t('login.checkEmail.title')}
          </h2>

          <div className="text-base" data-testid="text-check-email">
            {t('login.checkEmail.sentLink', { email })}
          </div>
          <div text-base data-testid="text-keep-page-open">
            {t('login.checkEmail.keepPageOpen')}
          </div>

          <NoAccountText className="mt-10">
            <Toast.Provider swipeDirection="right">
              <button
                className="codeButton"
                type="button"
                style={{ float: 'left', textDecoration: 'underline' }}
                onClick={async () => {
                  track('login_link_resent')
                  await triggerLogin()
                  setToastOpen(true)
                }}
                data-testid="btn-resend-link"
              >
                {t('login.resendLink')}
              </button>
              <Toast.Root
                className="ToastRoot"
                open={toastOpen}
                onOpenChange={setToastOpen}
              >
                {lastSendSucceeded && (
                  <Toast.Title className="ToastTitle">
                    {t('login.toast.emailSent')}
                  </Toast.Title>
                )}
                {!lastSendSucceeded && (
                  <Toast.Title className="ToastTitle">
                    {t('login.toast.unableToSend')}
                  </Toast.Title>
                )}
              </Toast.Root>
              <Toast.Viewport className="ToastViewport" />
            </Toast.Provider>
          </NoAccountText>
        </div>
      </PanelLayout>
    )
  }

  if (loading) {
    return (
      <PanelLayout titleKey="welcome.title">
        <div className="flex justify-center" style={{ paddingLeft: '135px' }}>
          <LoadingIcon />
        </div>
      </PanelLayout>
    )
  }

  return (
    <PanelLayout titleKey="welcome.title">
      <div className="flex flex-col gap-6 w-full ml-1">
        <h2 className="text-lg font-normal uppercase -mt-4">
          {t('login.form.title')}
        </h2>
        <TextInput
          autoFocus
          placeholder={t('login.form.emailPlaceholder')}
          type="email"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              triggerLogin()
            }
          }}
          value={email}
          data-testid="input-form-email"
        />
        {displayedError && (
          <div className="my-1 text-rose-600" data-testid="error-form-login">
            {displayedError}
          </div>
        )}
        <Button
          disabled={loading}
          onClick={() => triggerLogin()}
          className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
          data-testid="btn-submit-email-login"
        >
          {t('login.form.submitButton')}
        </Button>
        <NoAccountText>
          {t('login.noAccount.text')}{' '}
          <a
            className="cursor-pointer underline hover:text-clementine dark:hover:text-sun"
            onClick={() => navigate('/Signup')}
          >
            {t('login.noAccount.signUpLink')}
          </a>
          .
        </NoAccountText>
      </div>
    </PanelLayout>
  )
}

export const EmailLoginPanel = React.memo(EmailLoginPanelInner)
